import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from "react-redux";
import {Set as ImmSet, Map} from "immutable";

import {Spinner} from "@chakra-ui/react";
import {Virtuoso} from 'react-virtuoso';
import {ChevronDown} from "lucide-react";
import {capitalize} from "lodash";
import CardPost from '../../components/card_post';
import Comment from '../../components/comment';
import {WhiteMain} from '../../components/main';
import {FixedTopBar} from '../../components/top_bar';
import CommentPrompt from '../../components/create_prompt';
import PostActions from "../../components/post_actions";
import CommentActions from "../../components/comment_actions";
import ErrorMessage from "../../components/error_message";
import {usePost} from '../../api/post';
import useFeedback from '../../utils/use_feedback';
import {decodeBase62} from '../../utils/base62';
import {selectProfile} from "../../store/selectors";
import {useQuery} from "../../utils/router";
import {
    useBaseCommunity,
    useIsBlocked,
    useLoggedIn, usePostLink,
    useStoreComment,
    useStoreCommunity,
    useStoreProfile
} from "../../store";
import {useCommentThread} from "../../api/comment";
import {themecolors} from "../../utils/themecolors";
import Sheet from "../../components/bottom-sheet/sheet";
import CommentSortSheet from "../../components/bottom-sheet/comment_sort";
import UnderPostAdUnit from "../../components/ads/under_post_ad_unit";
import useHost from "../../utils/host";
import useWindowDimensions from "../../utils/dimensions";
import {CommentTextInputBox} from "../../components/create_comment";
import {Comment2, PostContext} from "../../components/comment2";

const Empty = styled.div``;
const CommentInfo = styled.div``;

const CommentCount = styled.div``;
const SingleComment = styled.div``;

const CommentOption = styled.span``;

const CreateCommentContainer = styled.div``;

const CommentWrapper = styled.div`
padding: 0 12px;`

const Options = styled.div``;
const Sort = styled.div``;
const SpinnerContainer = styled.div``;

const CommentThread = ({feedbackCh, commentId, commentSort, parentUuid, depth, postAuthor, blocked, setFirstThreadedComment}) => {
    const comment = useCommentThread(feedbackCh, commentId, commentSort, depth);
    setFirstThreadedComment(comment || false);
    return comment && !blocked?.includes(comment.author) && comment?.is_shown && <Comment2 depth={1} comment={comment} parentUuid={parentUuid} postAuthor={postAuthor} blocked={blocked}/>;
}

export const PostPage = styled(({className}) => {
    const {theme, blocked} = useStoreProfile();
    const loggedIn = useLoggedIn();
    const query = useQuery();
    const params = useParams();
    const history = useHistory();

    const {community} = params;
    const id = useMemo(() => decodeBase62(params.uuid), [params.uuid]);
    const depth = query.d ? parseInt(query.d) : false;
    const commentId = useMemo(() => Boolean(params.commentUuid) && decodeBase62(params.commentUuid), [params.commentUuid]);
    let {commentSort} = query;
    const defaultCommentSort = useSelector((state) => selectProfile(state)?.comment_sort);
    if (!commentSort) {
        commentSort = defaultCommentSort || 'top';
    }
    const {busy, error, feedbackCh} = useFeedback();
    const {post, comments} = usePost(feedbackCh, id, commentSort, !commentId);
    const storeCommunity = useStoreCommunity(post?.community);

    const handleViewAllComments = () => {
        if (!post) return;
        history.replace(`/c/${post.community}/p/${post.uuid}/x/c/`);
    };
    const handleViewParent = () => {
        if (!post) return;
        history.replace(`/c/${post.community}/p/${post.uuid}/x/c/${params.commentUuid}?d=${depth ? depth + 2 : 2}`);
    };

    // Scroll to top on mount and id change.
    useLayoutEffect(() => {
        document.scrollingElement.scrollTop = 0;
    }, [id]);

    const [sortOpen, setSortOpen] = useState(false);

    const contextComment = useStoreComment(commentId);
    const [firstThreadedComment, setFirstThreadedComment] = useState(false);

    useEffect(() => {
        setTimeout(function namedFunction() {
            document.scrollingElement.scrollTop = 0;
        }, 0);
    }, []);

    const host = useHost();
    const conditionIsMet = (post?.ad_tier && storeCommunity?.ad_tier) || (host === "scored.co" || host === "m.scored.co"
    || host === "m.communities.win" || host === "communities.win");

    const {width} = useWindowDimensions();

    const adImageUrl = useMemo(() => {
        return `https://ads.scored.co/api/pixel?fmt=mw-under&loggedIn=${loggedIn}&width=${width}&adTier=${post?.ad_tier}&host=${host}`;
    }, [post?.ad_tier]);

    const link = usePostLink(post);
    const [commentMap, setCommentMap] = useState(Map);
    const expandComment = useCallback((id) => {
        setCommentMap((m) => m.set(id, true));
    }, [setCommentMap]);

    const postContext = useMemo(
        () => ({post, link, commentMap, expandComment}),
        [post, link, commentMap, expandComment]
    );

    return (
        <div className={className}>
            <FixedTopBar title="Post" community={post?.community || community} back settings />
            <WhiteMain>
                <ErrorMessage error={error}/>
                <CardPost post={post}>
                    <PostActions post={post} inPost/>
                </CardPost>
                {conditionIsMet &&
                    <UnderPostAdUnit community={storeCommunity?.name} postAdTier={post?.ad_tier} />
                }
                {post && post.ad_tier &&
                    <div style={{position: 'absolute', top: 0, left: 0, zIndex: -9}}
                         className={`sponsored ad ad-unit google-ad googleAd google-sponsored-links ads4 fullSizeAd`}>
                        <img src={adImageUrl} />
                    </div>
                }
                {commentId &&
                        <CommentInfo>
                            {contextComment && firstThreadedComment.in_reply_to_id === 0 &&
                                <CommentCount>No earlier comments, <CommentOption onClick={handleViewAllComments}>view
                                    all</CommentOption></CommentCount>
                            }
                            {contextComment && firstThreadedComment.in_reply_to_id !== 0 &&
                                <CommentCount>Jump to <CommentOption onClick={handleViewParent}>earlier
                                    comment</CommentOption>, or <CommentOption onClick={handleViewAllComments}>view
                                    all</CommentOption></CommentCount>
                            }
                      </CommentInfo>
                }

                {comments && !busy && !post?.is_locked && !commentId &&
                    <CreateCommentContainer>
                        <CommentTextInputBox post={post} unopened hideCancel />
                    </CreateCommentContainer>
                }
                
                {!commentId &&
                        <Options>
                            <Sort onClick={() => setSortOpen(true)}>
                                Sort by {capitalize(commentSort)}
                                <ChevronDown size={16} />
                            </Sort>
                        </Options>
                }

                <PostContext.Provider value={postContext}>

                    <CommentWrapper>

                    {comments && comments.map((comment) => !blocked?.includes(comment.author) && comment?.is_shown && (
                        <Comment2
                            blocked={blocked}
                            key={comment.id}
                            comment={comment}
                            commentSort={post?.comment_sort}
                            depth={1}
                            postAuthor={post?.author}
                        />
                    ))}

                        {commentId && (
                            <CommentThread
                                commentId={commentId}
                                commentSort={post?.comment_sort}
                                depth={depth}
                                postAuthor={post?.author}
                                blocked={blocked}
                                setFirstThreadedComment={setFirstThreadedComment}
                            />
                        )}

                    {busy &&
                    <SpinnerContainer>
                        <Spinner
                            thickness='4px'
                            speed='0.65s'
                            emptyColor={theme === "dark" ? themecolors.dark_background_5 : themecolors.light_low_impact_text}
                            color={theme === "dark" ? themecolors.dark_background_2 : themecolors.pure_white}
                            size='lg'
                        />
                    </SpinnerContainer>
                    }

                    </CommentWrapper>

                </PostContext.Provider>


            </WhiteMain>
{/*            {post && (
                <CommentPrompt postId={post.id}/>
            )}*/}
            {/*{mobileCTAOpen && <Sheet onClose={() => handleMobileCTAClose() } padding={"header"}><MobileCTASheet onClose={() => handleMobileCTAClose() }/></Sheet>}*/}
            {sortOpen && <Sheet onClose={() => setSortOpen(false) } padding={"menu"}><CommentSortSheet community={community} sort={commentSort} uuid={post?.uuid} onClose={() => setSortOpen(false) }/></Sheet>}
        </div>
    )
})`
  .rtr-root {
    display: block;
  }

  ${WhiteMain} {
    margin-bottom: 60px;
  }

  ${Options} {
    display: flex;
    align-items: center;
    padding: 16px 20px 12px 20px;
  }
  ${SpinnerContainer} {
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_1 : themecolors.pure_white};
    text-align: center;
    padding: 48px 0;
  }

  ${Sort} {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }

  ${Sort} svg:first-of-type {
    margin-right: 8px;
    margin-left: 6px;
  }
  ${Empty} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
    text-align: center;
    padding-top: 48px;
    padding-bottom: 96px;
    font-size: 15px;
  }
  
  ${SingleComment} {
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
  }
  ${CommentOption} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.link : themecolors.link};
  }
  ${CommentInfo} {
    padding: 9px 20px;
    display: flex;
    flex-direction: column;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
    font-size: 14px;
    font-weight: 400;
  }
  ${CreateCommentContainer} {
    padding: 0 12px;
  }
`;

export default PostPage;
